import { getCurrentAwardSeason } from "./award-season";
import { PARTY_PASS_MARKETING_PRICE_CENTS, formatPriceLabel } from "./billing";
import { DEFAULT_FREE_PARTY_MEMBER_CAP } from "./party-entitlements";

const FREE_CAP = DEFAULT_FREE_PARTY_MEMBER_CAP;
const PARTY_PASS_PRICE = formatPriceLabel(PARTY_PASS_MARKETING_PRICE_CENTS);

export type AwardShowLandingCategory =
  | "film"
  | "tv"
  | "music"
  | "theater"
  | "guild"
  | "sports"
  | "pop";

export type AwardShowLanding = {
  slug: string;
  shortName: string;
  officialName: string;
  titleSingular: string;
  category: AwardShowLandingCategory;
  typicalMonth: number;
  typicalDay: number;
  featured: boolean;
  uniqueHook: string;
};

export type AwardShowLandingSection = {
  heading: string;
  body: string;
};

export type AwardShowLandingFaq = {
  question: string;
  answer: string;
};

export type AwardShowLandingCopy = {
  title: string;
  h1: string;
  intro: string;
  metaDescription: string;
  sections: AwardShowLandingSection[];
  faqs: AwardShowLandingFaq[];
  keywords: string[];
};

export const RESERVED_MARKETING_SLUGS = new Set([
  "sign-in",
  "sign-up",
  "join",
  "parties",
  "admin",
  "api",
  "recaps",
  "unsubscribe",
  "feedback",
  "contact",
  "award-shows",
  "shows",
  "guides",
  "blog",
  "winners",
  "robots.txt",
  "sitemap.xml",
  "llms.txt",
  "office-oscar-pool",
]);

export const AWARD_SHOW_LANDINGS: readonly AwardShowLanding[] = [
  {
    slug: "emmys",
    shortName: "Emmys",
    officialName: "Primetime Emmy Awards",
    titleSingular: "Emmy",
    category: "tv",
    typicalMonth: 9,
    typicalDay: 14,
    featured: true,
    uniqueHook:
      "The Primetime Emmys are the biggest night in television, with acting, series, limited series, and variety categories that make a private ballot with friends feel like its own watch party.",
  },
  {
    slug: "oscars",
    shortName: "Oscars",
    officialName: "Academy Awards",
    titleSingular: "Oscar",
    category: "film",
    typicalMonth: 3,
    typicalDay: 15,
    featured: true,
    uniqueHook:
      "The Oscars still set the movie-year conversation. A friends Oscar pool covers Best Picture through the crafts, so the whole ceremony stays competitive instead of turning into a spreadsheet.",
  },
  {
    slug: "grammys",
    shortName: "Grammys",
    officialName: "Grammy Awards",
    titleSingular: "Grammy",
    category: "music",
    typicalMonth: 2,
    typicalDay: 1,
    featured: true,
    uniqueHook:
      "Grammy night spans pop, rap, country, rock, and dozens of genre fields. A shared Grammy ballot lets your group pick the big four plus the categories you actually fight about.",
  },
  {
    slug: "golden-globes",
    shortName: "Golden Globes",
    officialName: "Golden Globe Awards",
    titleSingular: "Golden Globe",
    category: "film",
    typicalMonth: 1,
    typicalDay: 11,
    featured: true,
    uniqueHook:
      "The Golden Globes kick off awards season with film and TV in one room, including comedy and drama splits that make a friends ballot feel like two games in one.",
  },
  {
    slug: "tonys",
    shortName: "Tonys",
    officialName: "Tony Awards",
    titleSingular: "Tony",
    category: "theater",
    typicalMonth: 6,
    typicalDay: 7,
    featured: true,
    uniqueHook:
      "Tony night is Broadway's Super Bowl. A Tony prediction party is perfect for theater group chats, opening-night crews, and anyone who wants a live leaderboard during the broadcast.",
  },
  {
    slug: "baftas",
    shortName: "BAFTAs",
    officialName: "BAFTA Film Awards",
    titleSingular: "BAFTA",
    category: "film",
    typicalMonth: 2,
    typicalDay: 16,
    featured: true,
    uniqueHook:
      "BAFTA often reshapes Oscar narratives. A BAFTA pool with friends is a sharp way to play awards season before Hollywood's biggest night.",
  },
  {
    slug: "sag-awards",
    shortName: "SAG Awards",
    officialName: "Screen Actors Guild Awards",
    titleSingular: "SAG",
    category: "film",
    typicalMonth: 2,
    typicalDay: 23,
    featured: true,
    uniqueHook:
      "SAG is the actors honoring actors, including the ensemble categories that Oscar ballots skip. That makes a SAG prediction game with friends especially fun for cast-to-cast debates.",
  },
  {
    slug: "vmas",
    shortName: "VMAs",
    officialName: "MTV Video Music Awards",
    titleSingular: "VMA",
    category: "music",
    typicalMonth: 9,
    typicalDay: 7,
    featured: true,
    uniqueHook:
      "The VMAs are loud, live, and built for a watch party. Video of the Year, artist categories, and surprise performances make a VMA ballot a natural game for friend group chats.",
  },
  {
    slug: "pga-awards",
    shortName: "PGA Awards",
    officialName: "Producers Guild of America Awards",
    titleSingular: "PGA",
    category: "guild",
    typicalMonth: 1,
    typicalDay: 18,
    featured: true,
    uniqueHook:
      "The PGA Awards are one of the most-watched Oscar tells, especially for Best Picture. A PGA prediction party lets movie friends track the race before the Academy votes.",
  },
  {
    slug: "dga-awards",
    shortName: "DGA Awards",
    officialName: "Directors Guild of America Awards",
    titleSingular: "DGA",
    category: "guild",
    typicalMonth: 2,
    typicalDay: 7,
    featured: true,
    uniqueHook:
      "DGA is the directors' vote, and it often lines up with Oscar's Best Director. A DGA pool is a focused, high-signal game for film friends during peak awards season.",
  },
  {
    slug: "wga-awards",
    shortName: "WGA Awards",
    officialName: "Writers Guild of America Awards",
    titleSingular: "WGA",
    category: "guild",
    typicalMonth: 2,
    typicalDay: 21,
    featured: false,
    uniqueHook:
      "The WGA Awards celebrate original and adapted screenplays. A WGA ballot with friends is the writers-room version of awards season: original vs adapted, film and TV together.",
  },
  {
    slug: "billboard-music-awards",
    shortName: "Billboard Music Awards",
    officialName: "Billboard Music Awards",
    titleSingular: "Billboard Music Awards",
    category: "music",
    typicalMonth: 5,
    typicalDay: 15,
    featured: true,
    uniqueHook:
      "Billboard Music Awards follow the charts, so the game is part prediction and part 'who actually had the year.' A Billboard ballot is perfect for groups who argue about streams, tours, and hits.",
  },
  {
    slug: "critics-choice",
    shortName: "Critics Choice Awards",
    officialName: "Critics Choice Awards",
    titleSingular: "Critics Choice",
    category: "film",
    typicalMonth: 1,
    typicalDay: 12,
    featured: false,
    uniqueHook:
      "Critics Choice covers film and television just as awards season opens. It is an ideal early-year prediction party if your group wants a ballot before Oscars and Emmys heat up.",
  },
  {
    slug: "spirit-awards",
    shortName: "Spirit Awards",
    officialName: "Film Independent Spirit Awards",
    titleSingular: "Spirit Awards",
    category: "film",
    typicalMonth: 3,
    typicalDay: 1,
    featured: false,
    uniqueHook:
      "The Spirit Awards champion independent film. A Spirit Awards pool is the right game for friends who care about smaller movies as much as Best Picture frontrunners.",
  },
  {
    slug: "cma-awards",
    shortName: "CMA Awards",
    officialName: "Country Music Association Awards",
    titleSingular: "CMA",
    category: "music",
    typicalMonth: 11,
    typicalDay: 19,
    featured: false,
    uniqueHook:
      "CMA night is country's biggest broadcast. Entertainer of the Year, album, and song categories make a CMA prediction game a natural watch-party ballot for country fans.",
  },
  {
    slug: "latin-grammys",
    shortName: "Latin Grammys",
    officialName: "Latin Grammy Awards",
    titleSingular: "Latin Grammy",
    category: "music",
    typicalMonth: 11,
    typicalDay: 13,
    featured: false,
    uniqueHook:
      "The Latin Grammys cover pop, regional, urban, and tropical music in one ceremony. A Latin Grammy party with friends is a full-night ballot, not a one-category side game.",
  },
  {
    slug: "daytime-emmys",
    shortName: "Daytime Emmys",
    officialName: "Daytime Emmy Awards",
    titleSingular: "Daytime Emmy",
    category: "tv",
    typicalMonth: 10,
    typicalDay: 17,
    featured: false,
    uniqueHook:
      "Daytime Emmys honor soaps, talk, and daytime series that primetime ballots ignore. A Daytime Emmy prediction party is built for the groups who actually watch those shows together.",
  },
  {
    slug: "bet-awards",
    shortName: "BET Awards",
    officialName: "BET Awards",
    titleSingular: "BET Awards",
    category: "music",
    typicalMonth: 6,
    typicalDay: 9,
    featured: false,
    uniqueHook:
      "The BET Awards mix music, film, and television with a live-show energy that is made for a group chat leaderboard. Album, actor, and Viewer's Choice races keep the ballot interesting all night.",
  },
  {
    slug: "espy-awards",
    shortName: "ESPY Awards",
    officialName: "ESPY Awards",
    titleSingular: "ESPY",
    category: "sports",
    typicalMonth: 7,
    typicalDay: 16,
    featured: false,
    uniqueHook:
      "The ESPYs turn the sports year into one broadcast. Best athlete, team, and moment categories make an ESPY prediction game an easy watch-party pool for sports group chats.",
  },
  {
    slug: "peoples-choice",
    shortName: "People's Choice Awards",
    officialName: "People's Choice Awards",
    titleSingular: "People's Choice",
    category: "pop",
    typicalMonth: 2,
    typicalDay: 26,
    featured: false,
    uniqueHook:
      "People's Choice is the people's ballot across movies, TV, and music. That wide mix makes it a fun, low-pressure prediction party for friends who do not want a specialists-only Oscars sheet.",
  },
  {
    slug: "annie-awards",
    shortName: "Annie Awards",
    officialName: "Annie Awards",
    titleSingular: "Annie Awards",
    category: "film",
    typicalMonth: 2,
    typicalDay: 8,
    featured: false,
    uniqueHook:
      "The Annies are animation's own awards night. A dedicated Annie Awards ballot is perfect for friends who want to play Best Animated Feature the way animation fans actually watch it.",
  },
  {
    slug: "iheart-radio-awards",
    shortName: "iHeartRadio Music Awards",
    officialName: "iHeartRadio Music Awards",
    titleSingular: "iHeartRadio Music Awards",
    category: "music",
    typicalMonth: 3,
    typicalDay: 17,
    featured: false,
    uniqueHook:
      "iHeartRadio Music Awards blend fan-voted categories with radio hits. It is a lively music watch-party game if your group already argues about songs of the year.",
  },
  {
    slug: "brit-awards",
    shortName: "BRIT Awards",
    officialName: "BRIT Awards",
    titleSingular: "BRIT Awards",
    category: "music",
    typicalMonth: 3,
    typicalDay: 1,
    featured: false,
    uniqueHook:
      "The BRITs are the UK's flagship pop night. A BRIT Awards prediction party works for British friend groups and anyone who follows album and artist races on both sides of the Atlantic.",
  },
  {
    slug: "acm-awards",
    shortName: "ACM Awards",
    officialName: "Academy of Country Music Awards",
    titleSingular: "ACM",
    category: "music",
    typicalMonth: 5,
    typicalDay: 8,
    featured: false,
    uniqueHook:
      "ACM Awards hit earlier than the CMAs, so country fans get two ballots a year. An ACM pool with friends is a spring watch-party game with entertainer, album, and single races.",
  },
  {
    slug: "mtv-movie-awards",
    shortName: "MTV Movie & TV Awards",
    officialName: "MTV Movie & TV Awards",
    titleSingular: "MTV Movie & TV Awards",
    category: "pop",
    typicalMonth: 5,
    typicalDay: 19,
    featured: false,
    uniqueHook:
      "MTV Movie & TV Awards play the fun categories: fight scenes, performances, and fandom votes. That makes them an easy, unserious prediction game for a movie-night group.",
  },
] as const;

const landingsBySlug = new Map(
  AWARD_SHOW_LANDINGS.map((show) => [show.slug, show]),
);

export function getAwardShowLandingBySlug(
  slug: string,
): AwardShowLanding | undefined {
  return landingsBySlug.get(slug);
}

export function getAwardShowLandingByName(
  name: string,
): AwardShowLanding | undefined {
  return matchAwardShowLandingByName(name);
}

const WAITLIST_HEADLINE_SHOW_NAMES = new Set([
  "Oscars",
  "Emmys",
  "Grammys",
  "Tonys",
  "VMAs",
  "Golden Globes",
]);

const SHOW_NAME_STEMS: Record<string, string> = {
  emmys: "emmy",
  grammys: "grammy",
  oscars: "oscar",
  globes: "globe",
  vmas: "vma",
  baftas: "bafta",
  tonys: "tony",
  awards: "award",
};

const SHOW_NAME_FILLER = new Set(["award", "annual"]);

function stemShowToken(token: string): string {
  return SHOW_NAME_STEMS[token] ?? token;
}

function tokenizeShowName(name: string): string[] {
  return name
    .toLowerCase()
    .replace(/^the\s+/, "")
    .replace(/\b\d{4}\b/g, " ")
    .replace(/\b\d+(st|nd|rd|th)\b/g, " ")
    .replace(/\b\d{1,3}\b/g, " ")
    .replace(/\bannual\b/g, " ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map(stemShowToken);
}

function withoutFillerTokens(tokens: string[]): string[] {
  return tokens.filter((token) => !SHOW_NAME_FILLER.has(token));
}

function tokensEqual(left: string[], right: string[]): boolean {
  return (
    left.length === right.length &&
    left.every((token, i) => token === right[i])
  );
}

function containsConsecutiveTokens(
  haystack: string[],
  needle: string[],
): boolean {
  if (needle.length === 0 || needle.length > haystack.length) {
    return false;
  }

  for (let i = 0; i <= haystack.length - needle.length; i++) {
    if (needle.every((token, j) => haystack[i + j] === token)) {
      return true;
    }
  }

  return false;
}

function landingMatchesName(
  tokens: string[],
  landing: AwardShowLanding,
): boolean {
  const officialTokens = tokenizeShowName(landing.officialName);
  const shortTokens = tokenizeShowName(landing.shortName);
  const distinctiveOfficial = withoutFillerTokens(officialTokens);
  const distinctiveInput = withoutFillerTokens(tokens);

  return (
    tokensEqual(tokens, officialTokens) ||
    tokensEqual(tokens, shortTokens) ||
    tokensEqual(distinctiveInput, distinctiveOfficial) ||
    tokensEqual(distinctiveInput, shortTokens) ||
    containsConsecutiveTokens(tokens, officialTokens) ||
    (shortTokens.length >= 2 &&
      containsConsecutiveTokens(tokens, shortTokens)) ||
    (distinctiveOfficial.length >= 2 &&
      containsConsecutiveTokens(distinctiveInput, distinctiveOfficial)) ||
    (shortTokens.length >= 2 &&
      containsConsecutiveTokens(distinctiveInput, shortTokens))
  );
}

function matchAwardShowLandingByName(
  name: string,
): AwardShowLanding | undefined {
  const tokens = tokenizeShowName(name);
  if (tokens.length === 0) {
    return undefined;
  }

  const ranked = [...AWARD_SHOW_LANDINGS].sort((left, right) => {
    const officialDiff =
      withoutFillerTokens(tokenizeShowName(right.officialName)).length -
      withoutFillerTokens(tokenizeShowName(left.officialName)).length;
    if (officialDiff !== 0) {
      return officialDiff;
    }

    const shortDiff =
      tokenizeShowName(right.shortName).length -
      tokenizeShowName(left.shortName).length;
    if (shortDiff !== 0) {
      return shortDiff;
    }

    return right.officialName.length - left.officialName.length;
  });

  for (const landing of ranked) {
    if (landingMatchesName(tokens, landing)) {
      return landing;
    }
  }

  return undefined;
}

/** Conversational name for a known award show, independent of its stored title. */
export function getAwardShowShortName(name: string): string | null {
  return matchAwardShowLandingByName(name)?.shortName ?? null;
}

/**
 * Conversational show label for waitlist copy, e.g. "Emmys" or "Oscars".
 * Known major shows keep their short name. Custom or lesser-known shows
 * fall back to the current award season so the headline never uses a long
 * official or custom title.
 */
export function getWaitlistShowName(
  awardShowName?: string | null,
  now: Date = new Date(),
): string {
  const trimmed = awardShowName?.trim();
  if (trimmed) {
    const landing = matchAwardShowLandingByName(trimmed);
    if (landing && WAITLIST_HEADLINE_SHOW_NAMES.has(landing.shortName)) {
      return landing.shortName;
    }
  }

  return getCurrentAwardSeason(now).showName;
}

export function getWaitlistHeadline(
  awardShowName?: string | null,
  now: Date = new Date(),
): string {
  return `The party doesn't stop at the ${getWaitlistShowName(awardShowName, now)}.`;
}

export function getFeaturedAwardShowLandings(): AwardShowLanding[] {
  return AWARD_SHOW_LANDINGS.filter((show) => show.featured);
}

export function getAwardShowLandingPublicPaths(): string[] {
  return AWARD_SHOW_LANDINGS.map((show) => `/${show.slug}`);
}

export function getRelatedAwardShowLandings(
  slug: string,
  limit = 6,
): AwardShowLanding[] {
  const current = getAwardShowLandingBySlug(slug);
  if (!current) {
    return AWARD_SHOW_LANDINGS.filter((show) => show.slug !== slug).slice(
      0,
      limit,
    );
  }

  const sameCategory = AWARD_SHOW_LANDINGS.filter(
    (show) => show.slug !== slug && show.category === current.category,
  );
  const featured = AWARD_SHOW_LANDINGS.filter(
    (show) =>
      show.slug !== slug &&
      show.featured &&
      show.category !== current.category,
  );
  const rest = AWARD_SHOW_LANDINGS.filter(
    (show) =>
      show.slug !== slug &&
      show.category !== current.category &&
      !show.featured,
  );

  return [...sameCategory, ...featured, ...rest].slice(0, limit);
}

export function getAwardShowLandingYear(
  show: AwardShowLanding,
  now: Date = new Date(),
): number {
  const year = now.getFullYear();
  const ceremonyEnd = new Date(
    year,
    show.typicalMonth - 1,
    show.typicalDay,
    23,
    59,
    59,
    999,
  );
  if (now.getTime() > ceremonyEnd.getTime()) {
    return year + 1;
  }
  return year;
}

export function getAwardShowLandingTitle(
  show: AwardShowLanding,
  year: number,
): string {
  return `${year} ${show.titleSingular} Prediction Game for Friends | AwardParty`;
}

export function getAwardShowLandingH1(
  show: AwardShowLanding,
  year: number,
): string {
  return `${year} ${show.titleSingular} Prediction Game & Online Ballot`;
}

export function getAwardShowLandingIntro(
  show: AwardShowLanding,
  year: number,
): string {
  const article = indefiniteArticle(show.titleSingular);
  return `Create ${article} ${show.titleSingular} prediction party with friends, free for up to ${FREE_CAP} players. Pick the ${year} ${show.titleSingular} winners, invite everyone with one link, and see who's winning on a live leaderboard as the awards are announced.`;
}

function clipMetaDescription(text: string): string {
  if (text.length <= 160) {
    return text;
  }
  return `${text.slice(0, 157).trimEnd()}...`;
}

function indefiniteArticle(word: string): "a" | "an" {
  return /^[aeiou]/i.test(word) ? "an" : "a";
}

export function getAwardShowLandingCopy(
  show: AwardShowLanding,
  year: number,
): AwardShowLandingCopy {
  const intro = getAwardShowLandingIntro(show, year);
  const article = indefiniteArticle(show.titleSingular);
  const metaDescription = clipMetaDescription(
    `Create a private ${show.titleSingular} prediction party, free for up to ${FREE_CAP} players. Invite friends, make picks, and follow a live leaderboard.`,
  );

  const sections: AwardShowLandingSection[] = [
    {
      heading: `${show.titleSingular} prediction game with friends`,
      body: `${show.uniqueHook} AwardParty is ${article} ${show.titleSingular} prediction game for friends: create a private party, share one invite link, and keep score together instead of in a group chat screenshot. Everyone fills out the same ${year} ballot, then the live leaderboard updates as winners are called.`,
    },
    {
      heading: `Online ${show.titleSingular} ballot`,
      body: `Skip the printable grid. AwardParty gives you an online ${show.titleSingular} ballot with the categories ready to go. Friends pick on their phones, laptops, or the TV computer, and you can see who still needs to submit before picks lock.`,
    },
    {
      heading: `${show.titleSingular} watch party game`,
      body: `If you are hosting ${article} ${show.titleSingular} watch party, the game is the easiest way to keep people watching through the whole show. Put the live leaderboard on a second screen, trash-talk between categories, and crown a winner when the last envelope opens.`,
    },
    {
      heading: `${show.titleSingular} pool`,
      body: `${article === "an" ? "An" : "A"} ${show.titleSingular} pool used to mean a paper ballot and someone doing math at midnight. AwardParty runs the pool for you: same picks, automatic scoring, and a ranking everyone can refresh without asking 'who is winning?' in the chat.`,
    },
    {
      heading: `How ${show.titleSingular} prediction scoring works`,
      body: `${show.titleSingular} prediction scoring on AwardParty is simple: one point per correct winner. There is no mystery formula and no host spreadsheet. As official results come in, scores update and the leaderboard re-ranks instantly so the race stays visible all night.`,
    },
    {
      heading: `Live ${show.shortName} leaderboard`,
      body: `The live ${show.shortName} leaderboard is the reason to play in an app instead of on paper. When a winner is announced, points land immediately. You will know who jumped, who collapsed, and whether the night is over before the last award.`,
    },
    {
      heading: `When do ${show.shortName} picks lock?`,
      body: `The party host sets the ${show.titleSingular} prediction deadline, usually when the ceremony begins. After picks lock, nobody can sneak in a last-minute change. That keeps the ${show.titleSingular} pool fair and the stakes honest.`,
    },
    {
      heading: `Is the ${show.titleSingular} prediction game free?`,
      body: `Yes, for groups of up to ${FREE_CAP} people. You can create ${article} ${show.titleSingular} prediction party and start picking without a credit card. Bigger group? Party Pass is ${PARTY_PASS_PRICE}, one time per party. It removes the ${FREE_CAP} person limit and adds party chat, the end-of-show recap, and live reactions.`,
    },
    {
      heading: `How to invite friends to your ${show.shortName} party`,
      body: `Create your ${year} ${show.shortName} party, copy the invite link, and send it by text, iMessage, Slack, or email. Friends join in about a minute, fill out the online ${show.titleSingular} ballot, and they are on the leaderboard before the red carpet ends.`,
    },
  ];

  const faqs: AwardShowLandingFaq[] = [
    {
      question: `What is the AwardParty ${show.titleSingular} prediction game?`,
      answer: `AwardParty is ${article} ${show.titleSingular} prediction game for friends, free for parties of up to ${FREE_CAP} people. You create a private party, everyone submits an online ${show.titleSingular} ballot, and a live leaderboard tracks who is winning as the ${show.officialName} are announced.`,
    },
    {
      question: `Can I run ${article} ${show.titleSingular} pool with friends online?`,
      answer: `Yes. Create ${article} ${show.titleSingular} pool, invite your group with one link, and AwardParty scores the ballots automatically. It replaces printable ballots, group-chat tallies, and last-minute screenshot counting.`,
    },
    {
      question: `When do ${show.shortName} picks lock?`,
      answer: `The host chooses the deadline, typically the moment the ${show.officialName} broadcast begins. After that, predictions lock so the ${show.titleSingular} watch party game stays fair.`,
    },
    {
      question: `Is AwardParty's ${show.shortName} ballot free?`,
      answer: `Yes, for parties of up to ${FREE_CAP} people, and you can host a ${year} ${show.titleSingular} prediction party without a credit card. For a bigger group, Party Pass is a ${PARTY_PASS_PRICE} one-time upgrade for that party that removes the ${FREE_CAP} person limit.`,
    },
    {
      question: `How do I invite people to ${article} ${show.titleSingular} watch party game?`,
      answer: `Share your party link. Guests do not need a complicated setup: they open the link, join, and fill out the ${show.titleSingular} ballot on any device before picks lock.`,
    },
  ];

  const keywords = [
    `${show.shortName} prediction game`,
    `${show.titleSingular} prediction game with friends`,
    `online ${show.titleSingular} ballot`,
    `${show.titleSingular} watch party game`,
    `${show.shortName} pool`,
    `${show.shortName} predictions with friends`,
    `live ${show.shortName} leaderboard`,
    `free ${show.shortName} prediction app`,
  ];

  return {
    title: getAwardShowLandingTitle(show, year),
    h1: getAwardShowLandingH1(show, year),
    intro,
    metaDescription,
    sections,
    faqs,
    keywords,
  };
}
